<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useProposalsStore } from 'dashboard/stores/proposals';
import Button from 'dashboard/components-next/button/Button.vue';
import CreateProposalWizard from 'dashboard/components-next/Proposals/CreateProposalWizard.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const store = useProposalsStore();
const proposals = computed(() =>
  [...store.records].sort((a, b) => b.id - a.id)
);
const uiFlags = computed(() => store.uiFlags);
const wizardRef = ref(null);
const sendingEmailId = ref(null);
const sendingWhatsappId = ref(null);

const DELIVERY_BADGES = {
  draft: 'bg-n-slate-3 text-n-slate-11',
  generated: 'bg-n-slate-3 text-n-slate-11',
  email_sent: 'bg-n-amber-3 text-n-amber-11',
  whatsapp_sent: 'bg-n-amber-3 text-n-amber-11',
  sent: 'bg-n-teal-3 text-n-teal-11',
};
const deliveryBadgeClass = state =>
  DELIVERY_BADGES[state] || DELIVERY_BADGES.draft;
const deliveryLabel = state =>
  t(`PROPOSALS_MGMT.DELIVERY_STATE.${state.toUpperCase()}`);

const sendEmail = async proposal => {
  sendingEmailId.value = proposal.id;
  try {
    await store.sendEmail(proposal.id);
    useAlert(t('PROPOSALS_MGMT.SEND_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('PROPOSALS_MGMT.SEND_ERROR'));
  } finally {
    sendingEmailId.value = null;
  }
};

const sendWhatsapp = async proposal => {
  sendingWhatsappId.value = proposal.id;
  try {
    await store.sendWhatsapp(proposal.id);
    useAlert(t('PROPOSALS_MGMT.WIZARD.SEND_WHATSAPP_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t('PROPOSALS_MGMT.WIZARD.SEND_WHATSAPP_ERROR'));
  } finally {
    sendingWhatsappId.value = null;
  }
};

onMounted(() => store.get());
</script>

<template>
  <div class="flex flex-col w-full h-full gap-4 p-6 overflow-y-auto">
    <div class="flex items-center justify-between">
      <div class="flex flex-col gap-1">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('PROPOSALS_MGMT.PAGE_TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('PROPOSALS_MGMT.PAGE_DESCRIPTION') }}
        </p>
      </div>
      <Button
        :label="t('PROPOSALS_MGMT.NEW_PROPOSAL')"
        @click="wizardRef?.open()"
      />
    </div>

    <BaseTable
      :headers="[
        t('PROPOSALS_MGMT.TABLE.REFERENCE'),
        t('PROPOSALS_MGMT.TABLE.CONTACT'),
        t('PROPOSALS_MGMT.TABLE.SECTOR'),
        t('PROPOSALS_MGMT.TABLE.SOLUTION'),
        t('PROPOSALS_MGMT.TABLE.YEAR_1_TOTAL'),
        t('PROPOSALS_MGMT.TABLE.YEAR_2_TOTAL'),
        t('PROPOSALS_MGMT.TABLE.STATUS'),
        t('PROPOSALS_MGMT.TABLE.CREATED'),
        '',
      ]"
      :items="proposals"
      :no-data-message="
        uiFlags.fetchingList
          ? t('PROPOSALS_MGMT.LOADING')
          : t('PROPOSALS_MGMT.NO_DATA')
      "
    >
      <template #row="{ items }">
        <BaseTableRow
          v-for="proposal in items"
          :key="proposal.id"
          :item="proposal"
        >
          <template #default>
            <BaseTableCell>
              <span class="font-medium text-body-main text-n-slate-12">{{
                proposal.reference
              }}</span>
            </BaseTableCell>
            <BaseTableCell>{{ proposal.contact_name }}</BaseTableCell>
            <BaseTableCell>{{ proposal.sector_name }}</BaseTableCell>
            <BaseTableCell>
              {{ proposal.product_name }} -
              {{ proposal.plan_name }}
            </BaseTableCell>
            <BaseTableCell>
              {{ proposal.currency }}
              {{ proposal.year_1_total }}
            </BaseTableCell>
            <BaseTableCell>
              <template v-if="Number(proposal.recurring_annual_total) > 0">
                {{ proposal.currency }}
                {{ proposal.recurring_annual_total }}
                {{ t('PROPOSALS_MGMT.PER_YEAR') }}
              </template>
              <span v-else class="text-n-slate-10">&mdash;</span>
            </BaseTableCell>
            <BaseTableCell>
              <span
                class="px-2 py-0.5 text-xs rounded-full whitespace-nowrap"
                :class="deliveryBadgeClass(proposal.delivery_state)"
              >
                {{ deliveryLabel(proposal.delivery_state) }}
              </span>
            </BaseTableCell>
            <BaseTableCell>
              {{ new Date(proposal.created_at).toLocaleDateString() }}
            </BaseTableCell>
            <BaseTableCell align="end">
              <div class="flex justify-end gap-2">
                <a
                  v-if="proposal.pdf_url"
                  :href="proposal.pdf_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-xs font-medium text-n-blue-11 hover:underline"
                >
                  {{ t('PROPOSALS_MGMT.PDF') }}
                </a>
                <Button
                  v-if="!proposal.email_sent_at"
                  :label="t('PROPOSALS_MGMT.SEND')"
                  size="sm"
                  :is-loading="sendingEmailId === proposal.id"
                  @click="sendEmail(proposal)"
                />
                <Button
                  v-if="!proposal.whatsapp_sent_at"
                  :label="t('PROPOSALS_MGMT.SEND_WHATSAPP')"
                  size="sm"
                  variant="outline"
                  :is-loading="sendingWhatsappId === proposal.id"
                  @click="sendWhatsapp(proposal)"
                />
                <span
                  v-if="proposal.email_sent_at && proposal.whatsapp_sent_at"
                  class="text-xs text-n-teal-11"
                >
                  {{ t('PROPOSALS_MGMT.SENT') }}
                </span>
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <CreateProposalWizard ref="wizardRef" @created="store.get()" />
  </div>
</template>
